import React, { useEffect, useMemo, useState } from "react";

export default function AdminInvoicesEditor() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [err, setErr] = useState("");

  const token = localStorage.getItem("token"); // adjust if you store auth differently

  async function load() {
    setErr("");
    const r = await fetch("/api/invoices", {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) {
      setErr(`Failed to load invoices (${r.status})`);
      return;
    }
    const data = await r.json();
    // assumes your GET /api/invoices returns an array; adjust if it returns {invoices:[...]}
    const list = Array.isArray(data) ? data : (data.data || data.invoices || []);
    setRows(list);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      String(r.id).includes(s) ||
      String(r.invoice_number || "").toLowerCase().includes(s) ||
      String(r.created_at || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  function setRowField(id, field, val) {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: val } : r))
    );
  }

  async function saveRow(row) {
    setSavingId(row.id);
    setErr("");
    try {
      // UI uses date input "YYYY-MM-DD"; backend accepts date-only or full timestamp
      const payload = {
        invoice_number: (row.invoice_number || "").trim(),
        created_at: (row.created_at || "").trim(),
      };

      const r = await fetch(`/api/invoices/${row.id}/admin-update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await r.json().catch(() => ({}));
      if (!r.ok) {
        setErr(data.error || `Save failed (${r.status})`);
        return;
      }

      if (data.invoice) {
        setRows((prev) => prev.map((x) => (x.id === row.id ? { ...x, ...data.invoice } : x)));
      }
    } finally {
      setSavingId(null);
    }
  }

  // Helper: convert "YYYY-MM-DD HH:MM:SS" -> "YYYY-MM-DD" for <input type="date">
  function dateOnly(ts) {
    if (!ts) return "";
    return String(ts).slice(0, 10);
  }

  // Helper: keep existing time, just change date
  function applyDate(row, yyyyMmDd) {
    const old = String(row.created_at || "");
    const time = old.length >= 19 ? old.slice(11, 19) : "09:00:00";
    return `${yyyyMmDd} ${time}`;
  }

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: "0 auto" }}>
      <h2 style={{ margin: "0 0 12px" }}>Admin: Invoice Editor</h2>

      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by ID / invoice # / date..."
          style={{ padding: 10, minWidth: 280 }}
        />
        <button onClick={load} style={{ padding: "10px 14px", cursor: "pointer" }}>
          Refresh
        </button>
        {err ? <span style={{ color: "crimson" }}>{err}</span> : null}
      </div>

      <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: 10 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: "#f6f6f6" }}>
              <th style={th}>ID</th>
              <th style={th}>Invoice #</th>
              <th style={th}>Date</th>
              <th style={th}>Created At (raw)</th>
              <th style={th}>Total</th>
              <th style={th}>Paid</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered
              .slice()
              .sort((a, b) => b.id - a.id)
              .map((r) => (
                <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
                  <td style={tdMono}>{r.id}</td>

                  <td style={td}>
                    <input
                      value={r.invoice_number || ""}
                      onChange={(e) => setRowField(r.id, "invoice_number", e.target.value)}
                      style={{ padding: 8, width: 160 }}
                    />
                  </td>

                  <td style={td}>
                    <input
                      type="date"
                      value={dateOnly(r.created_at)}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRowField(r.id, "created_at", applyDate(r, v));
                      }}
                      style={{ padding: 8 }}
                    />
                  </td>

                  <td style={tdMono}>{r.created_at || ""}</td>
                  <td style={tdMono}>{r.total ?? ""}</td>
                  <td style={tdMono}>{String(r.paid ?? "")}</td>

                  <td style={td}>
                    <button
                      onClick={() => saveRow(r)}
                      disabled={savingId === r.id}
                      style={{ padding: "8px 12px", cursor: "pointer" }}
                    >
                      {savingId === r.id ? "Saving..." : "Save"}
                    </button>
                  </td>
                </tr>
              ))}
            {filtered.length === 0 ? (
              <tr>
                <td style={{ padding: 16 }} colSpan={7}>
                  No invoices found.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <p style={{ marginTop: 12, color: "#666" }}>
        Tip: The Date picker changes only the date and keeps the existing time (or uses 09:00:00 if missing).
      </p>
    </div>
  );
}

const th = { textAlign: "left", padding: 10, fontWeight: 700, fontSize: 14, whiteSpace: "nowrap" };
const td = { padding: 10, fontSize: 14, whiteSpace: "nowrap" };
const tdMono = { padding: 10, fontSize: 14, whiteSpace: "nowrap", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" };

